import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  DatePipe,
  formatNumber,
  NgComponentOutlet,
  NgTemplateOutlet,
} from '@angular/common';
import {
  afterNextRender,
  afterRenderEffect,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  linkedSignal,
  model,
  output,
  signal,
  TemplateRef,
  Type,
  untracked,
  viewChild,
} from '@angular/core';
import type { ValidationError, WithOptionalFieldTree } from '@angular/forms/signals';
import {
  AvatarComponent,
  BadgeComponent,
  BreakpointService,
  ButtonComponent,
  CheckboxComponent,
  ChipComponent,
  DsChipComponent,
  EmptyStateComponent,
  FieldRowComponent,
  IconComponent,
  InputComponent,
  ItemComponent,
  ItemLeadingComponent,
  ItemTitleComponent,
  ItemTrailingComponent,
  KbdComponent,
  LabelComponent,
  LoadingSkeletonComponent,
  LoadingSpinnerComponent,
  OverlayComponent,
  OverlayMode,
  RepeatDirective,
  SearchInputComponent,
  SearchInputSize,
  SoftSlotDirective,
} from '@softline/ui-core';
import {
  SelectDsComponent,
  SelectDsMultiComponent,
  SelectDsOptionComponent,
  SelectDsSectionActionComponent,
  SelectDsSectionComponent,
} from '@softline/ui-core/select';
import { DsTabsComponent } from '@softline/ui-core/tabs';
import {
  BookmarkCategory,
  FavoriteBookmarkBody,
  LastViewedEntityBookmarkBody,
} from '@softapps/core';
import { BookmarkRepository } from '@softapps/core';
import {
  FAVORITE_ENTITY_BOOKMARK_REPO,
  FavoriteEntityBookmarkRepository,
  LAST_SEARCH_BOOKMARK_REPO,
  LAST_VIEWED_ENTITY_BOOKMARK_REPO,
  LastSearchBookmarkRepository,
  LastViewedEntityBookmarkRepository,
} from '@softapps/core';
import { FavoriteEntityBookmarkService } from '@softapps/core';
import { LastSearchBookmarkService } from '@softapps/core';
import { LastViewedEntityBookmarkService } from '@softapps/core';
import {
  defaultKeyOf,
  findDirectKeyMatch,
} from '../../functions/direct-key-match.function';
import type {
  MasterdataBadge,
  MasterdataInputPresentation,
  MasterdataSearchColumn,
  MasterdataSearchModalTab,
} from '../../types';

const PAGE_JUMP = 10;

const EXPANDED_MODAL_GAP = '1rem';

const NON_TEXT_INPUT_TYPES = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'range',
  'file',
  'color',
]);

/** True when a keystroke on `target` would insert text there. */
function isTextEditable(target: EventTarget | null): boolean {
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPES.has(target.type);
  }
  return target instanceof HTMLElement && target.isContentEditable;
}

@Component({
  selector: 'soft-search-dropdown',
  templateUrl: './search-dropdown.component.html',
  styleUrls: ['./search-dropdown.component.css'],
  imports: [
    DatePipe,
    NgComponentOutlet,
    NgTemplateOutlet,
    CdkOverlayOrigin,
    AvatarComponent,
    ChipComponent,
    RepeatDirective,
    IconComponent,
    BadgeComponent,
    ButtonComponent,
    CheckboxComponent,
    DsChipComponent,
    DsTabsComponent,
    FieldRowComponent,
    InputComponent,
    LabelComponent,
    EmptyStateComponent,
    LoadingSkeletonComponent,
    LoadingSpinnerComponent,
    OverlayComponent,
    SearchInputComponent,
    SelectDsComponent,
    SelectDsMultiComponent,
    SelectDsOptionComponent,
    SelectDsSectionActionComponent,
    SelectDsSectionComponent,
    ItemComponent,
    ItemLeadingComponent,
    ItemTitleComponent,
    ItemTrailingComponent,
    KbdComponent,
    SoftSlotDirective,
  ],
  providers: [
    {
      provide: LAST_SEARCH_BOOKMARK_REPO,
      useFactory: () => {
        const config = inject(LastSearchBookmarkRepository, { optional: true });
        return new BookmarkRepository({
          category: BookmarkCategory.LastSearch,
          subCategory: config?.subCategory ?? 'entity-search',
        });
      },
    },
    { provide: BookmarkRepository, useExisting: LAST_SEARCH_BOOKMARK_REPO },
    {
      provide: LAST_VIEWED_ENTITY_BOOKMARK_REPO,
      useFactory: () => {
        const config = inject(LastViewedEntityBookmarkRepository, { optional: true });
        return new BookmarkRepository({
          category: BookmarkCategory.LastViewed,
          subCategory: config?.subCategory ?? 'entity-search',
        });
      },
    },
    {
      provide: FAVORITE_ENTITY_BOOKMARK_REPO,
      useFactory: () => {
        const config = inject(FavoriteEntityBookmarkRepository, { optional: true });
        return new BookmarkRepository({
          category: BookmarkCategory.Favorite,
          subCategory: config?.subCategory ?? 'entity-search',
        });
      },
    },
    LastSearchBookmarkService,
    LastViewedEntityBookmarkService,
    FavoriteEntityBookmarkService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.inline]': 'inline()',
    '[class.presentation-row]': 'isRow()',
    '[class.soft-ds-form-row]': 'isRow()',
  },
})
export class SearchDropdownComponent<T> {
  private readonly searchInput = viewChild<SearchInputComponent>('searchInput');
  private readonly modalSearchInput = viewChild<SearchInputComponent>('modalSearchInput');
  private readonly resultsRegion = viewChild<ElementRef<HTMLElement>>('resultsRegion');
  private readonly fieldRow = viewChild(FieldRowComponent);

  private readonly detailOutlet = viewChild(NgComponentOutlet);
  private detailCloseSub: { unsubscribe(): void } | null = null;
  private readonly wireDetailClose = effect(() => {
    this.detailCloseSub?.unsubscribe();
    this.detailCloseSub = null;
    const instance = this.detailOutlet()?.componentInstance as
      | { closePanel?: { subscribe(fn: () => void): { unsubscribe(): void } } }
      | null
      | undefined;
    this.detailCloseSub =
      instance?.closePanel?.subscribe(() => this.collapseDetail()) ?? null;
  });
  private readonly resultsListElement =
    viewChild<ElementRef<HTMLElement>>('resultsListElement');
  protected readonly triggerEl = viewChild<CdkOverlayOrigin>('trigger');
  readonly overlayWidth = computed(
    () => this.triggerEl()?.elementRef.nativeElement.offsetWidth ?? 0
  );

  readonly dropdownMaxHeight = signal<number>(400);

  private measureDropdownMaxHeight(): void {
    const el = this.triggerEl()?.elementRef.nativeElement;
    const win = typeof window !== 'undefined' ? window : null;
    if (!el || !win) return;
    const rect = el.getBoundingClientRect();
    const CHROME = 64;
    const below = win.innerHeight - rect.bottom - CHROME;
    const above = rect.top - CHROME;
    const MIN_BELOW = 180;
    const preferred = below >= MIN_BELOW ? below : Math.max(below, above);
    this.dropdownMaxHeight.set(Math.max(120, Math.round(preferred)));
  }

  public readonly selectedObject = model<any | null>(null);

  public readonly multiple = input<boolean>(false);

  public readonly selectedObjects = model<any[]>([]);

  public readonly confirmedMany = output<any[]>();

  public readonly previewObject = signal<any | null>(null);

  public readonly isChecked = (entity: any): boolean => {
    const key = this.entityTrack(entity);
    return this.selectedObjects().some((e) => this.entityTrack(e) === key);
  };

  public readonly clearPicks = (): void => {
    this.selectedObjects.set([]);
    this.confirmedMany.emit([]);
  };

  public readonly toggleChecked = (entity: any): void => {
    const key = this.entityTrack(entity);
    const current = this.selectedObjects();
    const next = this.isChecked(entity)
      ? current.filter((e) => this.entityTrack(e) !== key)
      : [...current, entity];
    this.selectedObjects.set(next);
  };

  public readonly allActiveChecked = computed<boolean>(() => {
    const rows = this.activeModalEntities();
    return rows.length > 0 && rows.every((e) => this.isChecked(e));
  });

  public readonly someActiveChecked = computed<boolean>(
    () =>
      !this.allActiveChecked() &&
      this.activeModalEntities().some((e) => this.isChecked(e))
  );

  public readonly toggleAllActive = (): void => {
    const rows = this.activeModalEntities();
    if (rows.length === 0) return;
    if (this.allActiveChecked()) {
      const pageKeys = new Set(rows.map((e) => this.entityTrack(e)));
      this.selectedObjects.set(
        this.selectedObjects().filter((e) => !pageKeys.has(this.entityTrack(e)))
      );
    } else {
      const current = this.selectedObjects();
      const missing = rows.filter((e) => !this.isChecked(e));
      this.selectedObjects.set([...current, ...missing]);
    }
  };

  public readonly confirmMultiSelection = (): void => {
    this.confirmedMany.emit(this.selectedObjects());
    if (this.multiSelectOverride()) {
      this.multiSelectOverride.set(false);
      this.selectedObjects.set([]);
    }
    this.suppressReopen = true;
    setTimeout(() => (this.suppressReopen = false));
    this.panelExpanded.set(false);
    this.searchFocused.set(false);
    this.isDropdownOpen.set(false);
    this.highlightedResultIndex.set(-1);
    this.previewObject.set(null);
    this.resetSearchTerm();
    this.resetInlineAfterClose();
  };

  public readonly searchFn =
    input.required<(searchTerm: string | undefined | null) => void>();

  public readonly abortInFlight = input<(() => void) | null>(null);

  public readonly loading = model<boolean>(false);
  public readonly value = model<string | undefined | null>('');
  public readonly _value = linkedSignal<string | undefined | null>(() => this.value());

  public readonly searchTerm = computed(() => this._value() ?? '');

  public readonly simplified = input<boolean>(false);
  public readonly label = input<string>('');
  public readonly showLabel = input<boolean>(true);
  public readonly readonly = input<boolean>(false);
  public readonly size = input<SearchInputSize>('md');
  public readonly title = input<string>('');
  public readonly overlayTitle = computed(() => this.title() || this.label());
  public readonly required = input<boolean>(false);
  public readonly icon = input<string>('fa-regular fa-database');
  public readonly placeholder = input<string>('Suchbegriff eingeben...');

  public readonly presentation = input<MasterdataInputPresentation>('default');

  public readonly errors = input<readonly WithOptionalFieldTree<ValidationError>[]>([]);
  public readonly invalid = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });
  public readonly isInvalid = computed(() => this.invalid() || this.errors().length > 0);

  public readonly isRow = computed(() => this.presentation() === 'row' && !this.inline());

  public readonly rowValue = computed<string | null>(() => {
    if (this.multiple()) return this.hasPicks() ? this.picksChipLabel() : null;
    const selected = this.selectedObject();
    return selected ? this.displayWith()(selected) : null;
  });

  public readonly rowSubvalue = computed<string | null>(() => {
    const selected = this.multiple() ? null : this.selectedObject();
    if (!selected) return null;
    return this.secondaryWith()?.(selected) ?? null;
  });

  public readonly searchResults = input<any[] | null | undefined>(null);

  public readonly totalCount = input<number | null>(null);

  public readonly hasMore = input<boolean>(false);
  public readonly loadingMore = input<boolean>(false);
  public readonly loadMore = output<void>();
  public readonly closed = output<void>();

  public readonly hasError = input<boolean>(false);
  public readonly errorTitle = input<string>('Suche fehlgeschlagen');
  public readonly errorMessage = input<string>(
    'Die Suche konnte nicht ausgeführt werden. Bitte erneut versuchen.'
  );
  public readonly retry = output<void>();

  public readonly resultTemplate = input<
    TemplateRef<{
      $implicit: any;
      searchTerm: string | null | undefined;
    }>
  >();
  public readonly selectedTemplate = input<
    TemplateRef<{
      $implicit: any;
    }>
  >();
  public readonly suggestions = input<boolean>(false);

  public readonly columns = input<MasterdataSearchColumn[]>([]);

  public readonly tableView = input<boolean>(false);

  public readonly inline = input<boolean>(false);

  public readonly detailSidebar = input<boolean>(false);

  public readonly detailComponent = input<Type<unknown> | null>(null);
  public readonly detailInputs = input<
    ((preview: any) => Record<string, unknown>) | null
  >(null);

  public readonly detailComponentInputs = computed<Record<string, unknown>>(() => {
    const build = this.detailInputs();
    const preview = this.previewObject();
    return build && preview ? build(preview) : {};
  });

  private readonly detailCollapsed = signal(true);

  public readonly showDetailPane = computed(
    () =>
      !!this.detailComponent() &&
      !!this.previewObject() &&
      !this.detailCollapsed() &&
      !this.mobileSheet()
  );

  public readonly canExpandDetail = computed(
    () =>
      !!this.detailComponent() &&
      !!this.previewObject() &&
      this.detailCollapsed() &&
      !this.mobileSheet()
  );

  public readonly collapseDetail = (): void => this.detailCollapsed.set(true);
  public readonly expandDetail = (): void => this.detailCollapsed.set(false);

  public readonly showExpandHint = input<boolean>(false);

  public readonly dropdownDisabled = input<boolean>(false);

  public readonly legacySlots = computed(
    () => this.dropdownDisabled() && !this.readonly()
  );

  public readonly showSpecialQueryKey = input<boolean>(false);

  public readonly showF4Hint = computed(
    () => this.showExpandHint() && !this.dropdownDisabled() && !this.readonly()
  );

  public readonly legacyHit = computed<{ direct: boolean; count: number } | null>(() => {
    if (!this.legacySlots() || !this.searchFocused()) return null;
    if (this.selectedObject() || !this._value()) return null;
    if (!this.searched() || this.searching()) return null;
    const count = (this.searchResults() ?? []).length;
    return { direct: !!this.directKeyMatch(), count };
  });

  public readonly onSlotMousedown = (event: Event): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  private readonly hostRef = inject(ElementRef<HTMLElement>);

  public readonly onTriggerFocusout = (event: FocusEvent): void => {
    const next = event.relatedTarget as HTMLElement | null;
    if (next && this.hostRef.nativeElement.contains(next)) return;
    if (next?.closest('.soft-ds-overlay')) return;
    if (this.panelExpanded()) return;
    if (!next) {
      if (this.isDropdownOpen()) return;
      this.searchFocused.set(false);
      return;
    }
    this.searchFocused.set(false);
    this.isDropdownOpen.set(false);
    this.highlightedResultIndex.set(-1);
  };

  public readonly openSpecialQuery = (event: Event): void => {
    event.stopPropagation();
    (event.currentTarget as HTMLElement).dispatchEvent(
      new KeyboardEvent('keydown', { key: 'F3', bubbles: true, cancelable: true })
    );
  };

  public readonly avatarWith = input<
    ((entity: any) => { initials?: string; src?: string | null }) | null
  >(null);

  public readonly avatarOf = (
    entity: any
  ): { initials: string; src: string | null } | null => {
    const fn = this.avatarWith();
    if (!fn) return null;
    const a = fn(entity) ?? {};
    return { initials: a.initials ?? '', src: a.src ?? null };
  };

  public readonly displayWith = input<(entity: any) => string>((entity) =>
    entity == null ? '' : String(entity.bezeichnung ?? entity.name1 ?? entity.name ?? '')
  );

  public readonly nameWith = input<((entity: any) => string | null) | null>(null);

  public readonly secondaryWith = input<((entity: any) => string | null) | null>(null);

  public readonly keyOf =
    input<(entity: any) => string | number | null | undefined>(defaultKeyOf);

  public readonly trackWith = input<((entity: any) => string | number) | null>(null);

  public readonly directKeyMatch = computed<any | null>(() =>
    findDirectKeyMatch(this.searchResults(), this._value(), this.keyOf())
  );

  public readonly triggerValue = computed<string>(() => {
    if (this.multiple()) {
      return this._value() ?? '';
    }
    const selected = this.selectedObject();
    if (selected) return this.displayWith()(selected);
    return this._value() ?? '';
  });

  public readonly triggerIcon = computed<string>(() => {
    if (this.selectedObject() || (this.multiple() && this.selectedObjects().length > 0))
      return this.icon();
    return 'fa-regular fa-magnifying-glass';
  });

  public readonly hasPicks = computed(
    () => this.multiple() && this.selectedObjects().length > 0
  );

  public readonly picksChipLabel = computed<string>(() => {
    const picks = this.selectedObjects();
    if (picks.length === 1) return this.displayWith()(picks[0]);
    return `${picks.length} ausgewählt`;
  });

  public readonly triggerTrailingIcon = computed<string | null>(() =>
    this.readonly() || this.legacySlots() ? null : 'fa-regular fa-database'
  );

  public readonly triggerLoading = computed<boolean>(
    () => this.loading() && !!this._value()
  );

  private readonly lastSearchBookmarkService = inject(LastSearchBookmarkService);
  readonly lastViewedEntityBookmarkService = inject(LastViewedEntityBookmarkService);
  readonly favoriteEntityBookmarkService = inject(FavoriteEntityBookmarkService);
  /** Not providedIn:'root' — provided by provideUiCore(). */
  private readonly breakpoints = inject(BreakpointService);

  public readonly sortedLastSearchResults = computed(() => {
    if (!this.bookmarksEnabled()) return [];
    const last = [...(this.lastSearchBookmarkService.all() ?? [])].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return [...new Map(last.map((b) => [b.key, b])).values()];
  });
  public readonly storageKey = input<string>('entity-search');

  public readonly bookmarksEnabled = input<boolean>(true);

  private readonly lastUsedLimit = 10;

  public readonly toFavoriteBody = input<((entity: any) => FavoriteBookmarkBody) | null>(
    null
  );
  public readonly toLastViewedBody = input<
    ((entity: any) => LastViewedEntityBookmarkBody) | null
  >(null);
  public readonly fromBookmarkBody = input<((body: any) => any) | null>(null);

  private readonly favoriteEntitiesLocal = signal<any[]>(
    this.loadStored(`${this.storageKey()}:favorites`)
  );
  private readonly lastUsedLocal = signal<any[]>(
    this.loadStored(`${this.storageKey()}:last-used`)
  );

  public readonly favoriteEntities = computed<any[]>(() => {
    if (!this.bookmarksEnabled()) return [];
    const from = this.fromBookmarkBody();
    if (!from) return this.dedupeByTrack(this.favoriteEntitiesLocal());
    return this.dedupeByTrack(
      (this.favoriteEntityBookmarkService.all() ?? []).map((b) => from(b.body))
    );
  });

  public readonly sortedLastUsedEntities = computed<any[]>(() => {
    if (!this.bookmarksEnabled()) return [];
    const from = this.fromBookmarkBody();
    if (!from) return this.dedupeByTrack(this.lastUsedLocal());
    const all = [...(this.lastViewedEntityBookmarkService.all() ?? [])].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return this.dedupeByTrack(all.map((b) => from(b.body)));
  });

  private loadStored(key: string): any[] {
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private persistStored(key: string, entities: any[]): void {
    try {
      localStorage.setItem(key, JSON.stringify(entities));
    } catch {}
  }

  private readonly pendingSearch = signal(false);
  public readonly searching = computed(() => this.pendingSearch() || this.loading());
  private readonly pendingDirectSelect = signal(false);
  public readonly searched = signal(false);
  public readonly searchFocused = signal(false);

  private suppressReopen = false;
  private lastScrolledIndex = -1;
  /** First row of the last anchored result set — detects a restarted query. */
  private anchoredFirstRow: unknown = null;
  public readonly highlightedResultIndex = signal(-1);

  private readonly tableNavigation = signal(false);

  public readonly tableRowKeyboard = computed(
    () => this.tableNavigation() && this.isExpandedView() && this.hasColumns()
  );

  public readonly hasHistory = computed(
    () =>
      this.sortedLastSearchResults().length > 0 ||
      this.favoriteEntities().length > 0 ||
      this.sortedLastUsedEntities().length > 0
  );

  public readonly navigatingResults = computed(
    () => !!this._value() || !this.hasHistory()
  );

  public readonly navigableEntities = computed<any[]>(() =>
    this.navigatingResults()
      ? (this.searchResults() ?? [])
      : [...this.favoriteEntities(), ...this.sortedLastUsedEntities()]
  );

  public readonly navRows = computed<any[]>(() =>
    this.isExpandedView() && !this.mobileSheet()
      ? this.activeModalEntities()
      : this.navigableEntities()
  );

  public readonly favoritesIndexOffset = computed(() =>
    this.navigatingResults() ? -1 : 0
  );
  public readonly lastUsedIndexOffset = computed(() =>
    this.navigatingResults() ? -1 : this.favoriteEntities().length
  );

  public readonly panelExpanded = model<boolean>(false);

  public readonly isExpandedView = computed(() => this.panelExpanded() || this.inline());

  public readonly mobileSheet = computed(
    () =>
      (this.breakpoints.isMobile() || this.isRow()) &&
      this.panelExpanded() &&
      !this.inline()
  );

  public readonly overlaySheetClass = computed(() =>
    this.mobileSheet() ? 'soft-ds-overlay-sheet' : null
  );

  public readonly multiToggle = input(false, { transform: booleanAttribute });
  /** Arm the Mehrfachauswahl automatically each time the extended search opens
   *  (like the Swing WebView's Artikelabfrage); the toggle can still turn it off. */
  public readonly multiToggleDefault = input(false, { transform: booleanAttribute });
  public readonly multiSelectOverride = signal(false);

  public readonly multiSelecting = computed(
    () => this.multiple() || this.multiSelectOverride()
  );

  protected toggleMultiSelect(): void {
    const next = !this.multiSelectOverride();
    this.multiSelectOverride.set(next);
    if (!next) this.selectedObjects.set([]);
    requestAnimationFrame(() => this.modalSearchInput()?.focus());
  }

  public readonly overlayMode = computed<OverlayMode>(() =>
    this.panelExpanded() ? 'modal' : 'connected'
  );

  public readonly modalTitle = input<string>('Erweiterte Suche');
  public readonly modalSubtitle = input<string>('');
  public readonly showAdvancedFilters = input<boolean>(false);
  public readonly typeFilter = model<string | null>(null);
  public readonly numberFilter = model<string | null>(null);
  public readonly cityFilter = model<string[] | null>(null);
  public readonly cityOptions = input<{ value: string; label: string }[]>([]);
  public readonly cityLabel = input<string>('Ort');
  public readonly cityEnabled = input<boolean | null>(null);
  protected readonly showCityFilter = computed(
    () => this.cityEnabled() ?? this.cityOptions().length > 0
  );
  public readonly cityOptionsCount = input<number | 'auto' | null>('auto');
  public readonly cityHasMore = input<boolean>(false);
  public readonly cityLoadingMore = input<boolean>(false);
  public readonly citySearch = output<string>();
  public readonly cityScrolledToEnd = output<void>();
  public readonly showDeleted = model<boolean>(false);
  public readonly showInactive = model<boolean>(false);
  public readonly typeOptions = input<{ value: string; label: string }[]>([]);
  public readonly typeLabel = input<string>('Typ');
  public readonly typeOptionsCount = input<number | 'auto' | null>('auto');
  public readonly numberLabel = input<string>('Nummer');
  public readonly showNumber = input<boolean, unknown>(true, {
    transform: booleanAttribute,
  });

  protected onNumberInput(value: string | null): void {
    const digits = (value ?? '').replace(/\D+/g, '');
    this.numberFilter.set(digits.length ? digits : null);
  }

  public readonly modalTab = signal<MasterdataSearchModalTab>('results');

  public readonly modalTabs = computed<
    { title: string; value: MasterdataSearchModalTab; badge: string | number }[]
  >(() => {
    const tabs: {
      title: string;
      value: MasterdataSearchModalTab;
      badge: string | number;
    }[] = [{ title: 'Ergebnisse', value: 'results', badge: this.resultCountLabel() }];
    if (this.bookmarksEnabled()) {
      tabs.push(
        { title: 'Favoriten', value: 'favorites', badge: this.favoriteEntities().length },
        {
          title: 'Zuletzt verwendet',
          value: 'last-used',
          badge: this.sortedLastUsedEntities().length,
        }
      );
    }
    if (this.multiSelecting()) {
      tabs.push({
        title: 'Ausgewählte',
        value: 'selected',
        badge: this.selectedObjects().length,
      });
    }
    return tabs;
  });

  public readonly activeModalEntities = computed<any[]>(() => {
    switch (this.modalTab()) {
      case 'favorites':
        return this.favoriteEntities();
      case 'last-used':
        return this.sortedLastUsedEntities();
      case 'selected':
        return this.dedupeByTrack(this.selectedObjects());
      default:
        return this.visibleSearchResults();
    }
  });

  public readonly hasColumns = computed(() => this.tableView());

  public readonly usePreviewFlow = computed(
    () =>
      !this.multiSelecting() &&
      this.isExpandedView() &&
      (this.tableView() || this.mobileSheet())
  );

  /** The detail preview follows the keyboard highlight — the single-select preview
   *  flow plus the multiselect table, whose sidebar previews the highlighted row
   *  (click and Space already do; arrow keys must match). */
  public readonly highlightDrivesPreview = computed(
    () =>
      this.usePreviewFlow() ||
      (this.multiSelecting() && this.isExpandedView() && this.hasColumns())
  );

  public readonly expandedWidthValue = computed(() => {
    if (this.mobileSheet()) return '100%';
    return this.detailSidebar()
      ? `min(2400px, 100vw - 2 * ${EXPANDED_MODAL_GAP})`
      : `min(1400px, 100vw - 2 * ${EXPANDED_MODAL_GAP})`;
  });

  public readonly expandedMaxHeightValue = computed(() =>
    this.mobileSheet() ? '100%' : `calc(100dvh - 2 * ${EXPANDED_MODAL_GAP})`
  );

  public readonly overlayMaxWidth = computed(() =>
    this.mobileSheet() ? '100%' : '120vw'
  );

  public readonly effectiveColumns = computed<MasterdataSearchColumn[]>(() => {
    const explicit = this.columns();
    if (explicit.length > 0) return explicit;
    if (!this.tableView()) return [];
    const keyOf = this.keyOf();
    const nameOf = this.nameWith() ?? this.displayWith();
    const secondaryOf = this.secondaryWith();
    return [
      {
        label: this.label() || 'Eintrag',
        type: 'entity',
        icon: this.icon(),
        value: (entity: any) => nameOf(entity),
        secondary: secondaryOf ? (entity: any) => secondaryOf(entity) : undefined,
      },
      {
        label: 'Nr.',
        value: (entity: any) => keyOf(entity),
      },
    ];
  });

  public readonly colspan = computed(
    () =>
      this.effectiveColumns().length +
      (this.bookmarksEnabled() ? 1 : 0) +
      (this.multiSelecting() ? 1 : 0)
  );

  /** Badge tone → DS badge variant (mirrors masterdata-input.badgeVariant). */
  public readonly badgeVariant = (
    badge: MasterdataBadge
  ): 'success' | 'warning' | 'danger' | 'secondary' => {
    switch (badge.tone) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'danger':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  public readonly columnTitle = (col: MasterdataSearchColumn, entity: any): string =>
    this.cellValue(col, entity);
  public readonly columnSecondary = (
    col: MasterdataSearchColumn,
    entity: any
  ): string | null => {
    const v = col.secondary?.(entity);
    return v === null || v === undefined || v === '' ? null : String(v);
  };
  public readonly columnBadge = (
    col: MasterdataSearchColumn,
    entity: any
  ): MasterdataBadge | null => col.badge?.(entity) ?? null;

  public readonly detailRows = computed<{ label: string; value: string }[]>(() => {
    const entity = this.previewObject();
    if (!entity) return [];
    return this.effectiveColumns().map((col) => {
      let value: string;
      if (col.type === 'badge') value = this.columnBadge(col, entity)?.label ?? '';
      else value = this.cellValue(col, entity);
      return { label: col.label, value };
    });
  });

  private readonly resultsTabPromptToSearch = computed(
    () => !this._value() && !this.searched()
  );

  public readonly modalEmptyTitle = computed(() => {
    switch (this.modalTab()) {
      case 'favorites':
        return 'Keine Favoriten';
      case 'last-used':
        return 'Noch nichts verwendet';
      case 'selected':
        return 'Nichts ausgewählt';
      default:
        return this.resultsTabPromptToSearch()
          ? 'Suchbegriff eingeben'
          : 'Keine Ergebnisse gefunden';
    }
  });
  public readonly modalEmptyDescription = computed(() => {
    switch (this.modalTab()) {
      case 'favorites':
        return 'Markieren Sie Einträge mit dem Stern, um sie hier zu sammeln.';
      case 'last-used':
        return 'Zuletzt ausgewählte Einträge erscheinen hier.';
      case 'selected':
        return 'Markieren Sie Einträge, um sie hier zu sammeln.';
      default:
        return this.resultsTabPromptToSearch()
          ? this.placeholder()
          : 'Keine Einträge entsprechen den Suchkriterien.';
    }
  });

  private readonly shouldOpenDropdown = computed(
    () => this.searchFocused() && !this.dropdownDisabled() && !this.isRow()
  );

  public readonly isDropdownOpen = linkedSignal(() => this.shouldOpenDropdown());

  public readonly showResultsHeader = computed(
    () =>
      (this.searchResults()?.length ?? 0) > 0 &&
      !this.loading() &&
      !this.searching() &&
      (this.searched() || this.suggestions())
  );

  public readonly resultCount = computed(
    () => this.totalCount() ?? this.searchResults()?.length ?? 0
  );

  public readonly resultCountLabel = computed(() =>
    formatNumber(this.resultCount(), 'de')
  );

  public readonly resultsSectionLabel = computed(() =>
    this._value() ? 'Ergebnisse' : 'Vorschläge'
  );

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event) {
    if (!this.searchFocused()) {
      return;
    }
    this.cancelPendingSearch();
    this.pendingDirectSelect.set(false);
    this.loading.set(false);
    this.searchFocused.set(false);
    this.isDropdownOpen.set(false);
    this.panelExpanded.set(false);
    this.resetSearchTerm();
    this.restoreTriggerFocus();
    this.resetInlineAfterClose();
  }

  /** In multiselect, Space stays a table key while the table owns the focus:
   *  it checks the highlighted row and never scrolls. In single select it types
   *  like any other character (see onDocumentTypeToSearch). */
  @HostListener('document:keydown.space', ['$event'])
  onSpace(event: Event) {
    if (!this.multiSelecting() || !this.tableRowKeyboard()) return;
    event.preventDefault();
    const row = this.highlightedRow();
    if (!row) return;
    this.toggleChecked(row);
    this.previewObject.set(row);
  }

  @HostListener('document:keydown.control.f', ['$event'])
  @HostListener('document:keydown.meta.f', ['$event'])
  onCtrlF(event: Event) {
    if (!this.isExpandedView()) return;
    event.preventDefault();
    this.focusSearchField();
  }

  /** Typing a character anywhere in the expanded/inline view returns the focus
   *  to the search field; the keystroke's default action then lands in the
   *  refocused input. This must not depend on the table owning the keyboard —
   *  after a checkbox/star/neutral click nothing holds the focus and typing
   *  would otherwise die. Space is excluded only in multiselect (table key —
   *  it checks the row); shortcuts and typing that already targets a
   *  text-editable element (e.g. the advanced filter fields) stay put. */
  onDocumentTypeToSearch(event: KeyboardEvent) {
    if (!this.isExpandedView()) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key.length !== 1) return;
    if (event.key === ' ' && this.multiSelecting()) return;
    if (isTextEditable(event.target)) return;
    this.focusSearchField();
  }

  @HostListener('window:blur')
  onWindowBlur() {
    if (this.panelExpanded() || this.inline() || !this.isDropdownOpen()) return;
    this.searchFocused.set(false);
    this.isDropdownOpen.set(false);
    this.highlightedResultIndex.set(-1);
  }

  @HostListener('document:keydown.enter', ['$event'])
  onEnter(event: Event) {
    if (!this.isSurfaceOpen()) return;
    const rows = this.navRows();
    const i = this.highlightedResultIndex();
    const highlighted = i >= 0 && i < rows.length ? rows[i] : null;
    const row = highlighted ?? (this.usePreviewFlow() ? this.previewObject() : null);
    if (!row) {
      this.onEnterInExpandedField(event);
      return;
    }
    event.preventDefault();
    if (this.tableRowKeyboard()) {
      if (this.multiSelecting()) {
        if (this.selectedObjects().length === 0) this.toggleChecked(row);
        this.confirmMultiSelection();
        return;
      }
      this.previewObject.set(row);
      this.confirmPreview();
      return;
    }
    if (this.multiSelecting()) {
      this.toggleChecked(row);
      this.previewObject.set(row);
    } else if (this.panelExpanded()) {
      this.previewObject.set(row);
      if (!this.mobileSheet()) this.selectResultFromModal(row);
    } else if (this.navigatingResults()) {
      this.selectResult(row);
    } else {
      this.selectFromHistory(row);
    }
  }

  @HostListener('document:keydown.control.enter', ['$event'])
  @HostListener('document:keydown.meta.enter', ['$event'])
  onCtrlEnter(event: Event) {
    if (!this.isSurfaceOpen()) return;
    if (this.multiSelecting()) {
      if (this.selectedObjects().length === 0) return;
      event.preventDefault();
      this.confirmMultiSelection();
      return;
    }
    const rows = this.navRows();
    const i = this.highlightedResultIndex();
    const row = i >= 0 && i < rows.length ? rows[i] : this.previewObject();
    if (!row) return;
    event.preventDefault();
    if (this.isExpandedView()) {
      this.previewObject.set(row);
      this.confirmPreview();
    } else if (this.navigatingResults()) {
      this.selectResult(row);
    } else {
      this.selectFromHistory(row);
    }
  }

  @HostListener('document:keydown.tab', ['$event'])
  onTab(event: Event) {
    if (!this.searchFocused()) {
      return;
    }
  }

  @HostListener('document:keydown.f4', ['$event'])
  onF4(event: Event) {
    if (this.inline()) return;
    // a filled field never sets searchFocused (setFocused bails on a selection)
    // — F4 must still open the extended search while the trigger has focus
    if (!this.searchFocused() && !this.triggerHasFocus()) {
      return;
    }
    event.preventDefault();
    if (this.panelExpanded()) this.panelExpanded.set(false);
    else this.openExpanded();
  }

  private triggerHasFocus(): boolean {
    const active = this.hostRef.nativeElement.ownerDocument.activeElement;
    return !!active && this.hostRef.nativeElement.contains(active);
  }

  /** The ONE document:keydown listener — Angular keys host listeners by event
   *  name, so a second identical @HostListener declaration is silently dropped. */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent) {
    this.onDocumentKeydownForSidebar(event);
    this.onDocumentTypeToSearch(event);
  }

  onDocumentKeydownForSidebar(event: KeyboardEvent) {
    if (!event.ctrlKey && !event.metaKey) return;
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    if (!this.isExpandedView() || !this.detailComponent() || !this.previewObject())
      return;
    event.preventDefault();
    if (event.key === 'ArrowLeft') this.expandDetail();
    else this.collapseDetail();
  }

  @HostListener('document:keydown.f12', ['$event'])
  onF12(event: Event) {
    if (this.multiSelecting()) {
      if (this.selectedObjects().length === 0) return;
      event.preventDefault();
      this.confirmMultiSelection();
      return;
    }
    if (!this.isExpandedView() || !this.usePreviewFlow() || !this.previewObject()) {
      return;
    }
    event.preventDefault();
    this.confirmPreview();
  }

  private readonly isSurfaceOpen = computed(
    () => this.isExpandedView() || this.isDropdownOpen()
  );

  @HostListener('document:keydown.arrowdown', ['$event'])
  onDocumentArrowDown(event: Event) {
    if (!this.isSurfaceOpen()) return;
    this.moveHighlight(1, event);
  }

  @HostListener('document:keydown.arrowup', ['$event'])
  onDocumentArrowUp(event: Event) {
    if (!this.isSurfaceOpen()) return;
    this.moveHighlight(-1, event);
  }

  @HostListener('document:keydown.pagedown', ['$event'])
  onPageDown(event: Event) {
    if (!this.isSurfaceOpen()) return;
    this.jumpHighlight(Math.max(this.highlightedResultIndex(), 0) + PAGE_JUMP, event);
  }

  @HostListener('document:keydown.pageup', ['$event'])
  onPageUp(event: Event) {
    if (!this.isSurfaceOpen()) return;
    this.jumpHighlight(Math.max(this.highlightedResultIndex(), 0) - PAGE_JUMP, event);
  }

  @HostListener('document:keydown.home', ['$event'])
  onHome(event: Event) {
    if (!this.isSurfaceOpen() || this.caretOwnsHomeEnd(event)) return;
    this.jumpHighlight(0, event);
  }

  @HostListener('document:keydown.end', ['$event'])
  onEnd(event: Event) {
    if (!this.isSurfaceOpen() || this.caretOwnsHomeEnd(event)) return;
    this.jumpHighlight(this.navRows().length - 1, event);
  }

  private caretOwnsHomeEnd(event: Event): boolean {
    const target = event.target;
    return (
      (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) &&
      !!target.value
    );
  }

  private jumpHighlight(target: number, event: Event): void {
    const entities = this.navRows();
    if (entities.length === 0) return;
    event.preventDefault();
    this.focusResults();
    const next = Math.max(0, Math.min(entities.length - 1, target));
    this.highlightedResultIndex.set(next);
    if (this.highlightDrivesPreview()) {
      this.previewObject.set(entities[next]);
    }
  }

  private scrollRowIntoTable(row: HTMLElement): boolean {
    const scroller = row.closest(
      '.masterdata-search-modal-content'
    ) as HTMLElement | null;
    const headerCell = scroller?.querySelector(
      '.masterdata-search-table thead th'
    ) as HTMLElement | null;
    if (!scroller || !headerCell) return false;

    // layout offsets, not bounding rects: the expanded pane is still scaled by
    // its enter animation when the first page key lands, and a scaled rect
    // yields a delta that falls short of the row
    const rowTop = layoutTop(row) - layoutTop(scroller) - scroller.clientTop;
    const rowBottom = rowTop + row.offsetHeight;
    const reserved = parseFloat(getComputedStyle(scroller).paddingBottom) || 0;
    const viewTop = scroller.scrollTop + headerCell.offsetHeight;
    const viewBottom = scroller.scrollTop + scroller.clientHeight - reserved;
    if (rowTop < viewTop) scroller.scrollTop -= viewTop - rowTop;
    else if (rowBottom > viewBottom) scroller.scrollTop += rowBottom - viewBottom;
    return true;
  }

  private highlightedRow(): any | null {
    const rows = this.navRows();
    const i = this.highlightedResultIndex();
    return i >= 0 && i < rows.length ? rows[i] : null;
  }

  /** There is only ONE focus in the expanded view — either the search field or
   *  the table. Any gesture that moves the row highlight (arrows, page keys, a
   *  row click, a seeded search) hands the focus to the table: the field loses
   *  its caret, the anchor effect highlights row 0 if nothing is highlighted. */
  public readonly focusResults = (): void => {
    if (!this.isExpandedView() || !this.hasColumns()) return;
    this.tableNavigation.set(true);
    // Give the results region REAL DOM focus instead of blurring into the void:
    // embedded hosts (JxBrowser) only deliver keystrokes reliably to a focused
    // element, and focusing it implicitly releases the caret from both search
    // fields (the collapsed trigger keeps it otherwise — no focus trap).
    const region = this.resultsRegion()?.nativeElement;
    if (region) {
      region.focus({ preventScroll: true });
      return;
    }
    this.modalSearchInput()?.blur();
    this.searchInput()?.blur();
  };

  /** Hands the single focus back to the search field — the table highlight
   *  (blue frame) goes away. Reached via Ctrl+F, ArrowUp on the first row, or
   *  typing a character while the table owns the keyboard. */
  public readonly focusSearchField = (): void => {
    this.tableNavigation.set(false);
    this.highlightedResultIndex.set(-1);
    this.previewObject.set(null);
    this.modalSearchInput()?.focus();
  };

  private moveHighlight(delta: 1 | -1, event: Event): void {
    const entities = this.navRows();
    if (entities.length <= 0) return;
    event.preventDefault();
    if (delta === -1 && this.tableRowKeyboard() && this.highlightedResultIndex() === 0) {
      this.focusSearchField();
      return;
    }
    this.focusResults();
    const current = this.highlightedResultIndex();
    const valid = current >= 0 && current < entities.length;
    const next = valid
      ? (current + delta + entities.length) % entities.length
      : delta === 1
        ? 0
        : entities.length - 1;
    this.highlightedResultIndex.set(next);
    if (this.highlightDrivesPreview()) {
      this.previewObject.set(entities[next]);
    }
  }

  public readonly search = (): void => {
    const searchInputValue = this._value();
    this.pendingSearch.set(false);
    this.highlightedResultIndex.set(-1);
    this.value.set(searchInputValue);
    this.searched.set(true);
    this.searchFn()?.(searchInputValue);
  };

  private recordLastSearch(): void {
    if (!this.bookmarksEnabled()) return;
    const term = this._value()?.trim();
    if (term) this.lastSearchBookmarkService.search(term);
  }

  public readonly deleteAllLastSearchResults = (): void => {
    this.lastSearchBookmarkService.deleteAll();
  };

  public readonly deleteLastSearchResult = (key: string): void => {
    this.lastSearchBookmarkService.delete(key);
  };

  private favoriteKey = (entity: any): unknown => {
    if (entity == null) return entity;
    const track = this.trackWith();
    if (track) return track(entity);
    const key = this.keyOf()(entity);
    return key ?? entity?.id ?? entity;
  };

  public readonly isFavorite = (entity: any): boolean => {
    const toBody = this.toFavoriteBody();
    if (toBody) return this.favoriteEntityBookmarkService.isFavorite(toBody(entity).key);
    return this.favoriteEntitiesLocal().some(
      (e) => this.favoriteKey(e) === this.favoriteKey(entity)
    );
  };

  public readonly entityTrack = (entity: any): unknown => this.favoriteKey(entity);

  private readonly dedupeByTrack = (entities: any[]): any[] => {
    if (entities.length < 2) return entities;
    const byKey = new Map<unknown, any>();
    for (const entity of entities) {
      const key = this.entityTrack(entity);
      if (!byKey.has(key)) byKey.set(key, entity);
    }
    return byKey.size === entities.length ? entities : [...byKey.values()];
  };

  public readonly visibleSearchResults = computed<any[]>(() =>
    this.dedupeByTrack(this.searchResults() ?? [])
  );

  public readonly cellValue = (column: MasterdataSearchColumn, entity: any): string => {
    const v = column.value?.(entity);
    return v === null || v === undefined ? '' : String(v);
  };

  public readonly cellTone = (
    column: MasterdataSearchColumn,
    entity: any
  ): 'danger' | null => column.tone?.(entity) ?? null;

  public readonly setFavorite = (entity: any) => {
    if (!this.bookmarksEnabled()) return;
    const toBody = this.toFavoriteBody();
    if (toBody) {
      void this.favoriteEntityBookmarkService.toggle(toBody(entity));
      return;
    }
    const key = this.favoriteKey(entity);
    const current = this.favoriteEntitiesLocal();
    const next = this.isFavorite(entity)
      ? current.filter((e) => this.favoriteKey(e) !== key)
      : [...current, entity];
    this.favoriteEntitiesLocal.set(next);
    this.persistStored(`${this.storageKey()}:favorites`, next);
  };

  public readonly setLastUsed = (entity: any) => {
    if (entity == null || !this.bookmarksEnabled()) return;
    const toBody = this.toLastViewedBody();
    if (toBody) {
      void this.lastViewedEntityBookmarkService.view(toBody(entity));
      return;
    }
    const key = this.favoriteKey(entity);
    const withoutDupe = this.lastUsedLocal().filter((e) => this.favoriteKey(e) !== key);
    const next = [entity, ...withoutDupe].slice(0, this.lastUsedLimit);
    this.lastUsedLocal.set(next);
    this.persistStored(`${this.storageKey()}:last-used`, next);
  };

  public readonly clearLastUsed = (): void => {
    if (this.toLastViewedBody() || this.fromBookmarkBody()) {
      void this.lastViewedEntityBookmarkService.deleteAll();
      return;
    }
    this.lastUsedLocal.set([]);
    this.persistStored(`${this.storageKey()}:last-used`, []);
  };

  public readonly clearFavorites = (): void => {
    if (this.toFavoriteBody() || this.fromBookmarkBody()) {
      void this.favoriteEntityBookmarkService.deleteAll();
      return;
    }
    this.favoriteEntitiesLocal.set([]);
    this.persistStored(`${this.storageKey()}:favorites`, []);
  };

  public readonly setFocused = () => {
    if (this.readonly()) return;
    if (this.suppressReopen) {
      this.suppressReopen = false;
      return;
    }
    if (this.selectedObject()) {
      return;
    }
    this.measureDropdownMaxHeight();
    this.searchFocused.set(true);
    if (!this.dropdownDisabled() && !this.isRow()) this.isDropdownOpen.set(true);
    if (this.bookmarksEnabled()) {
      this.lastSearchBookmarkService.load();
      this.lastViewedEntityBookmarkService.load();
      this.favoriteEntityBookmarkService.load();
    }
    this.searchInput()?.focus();
    if (
      !this._value() &&
      !this.selectedObject() &&
      !this.searched() &&
      !this.searching()
    ) {
      this.prefetch();
    }
  };

  public readonly prefetch = (): void => {
    this.searched.set(true);
    this.searchFn()?.('');
  };

  public readonly blurAndClose = (): void => {
    this.cancelPendingSearch();
    this.pendingDirectSelect.set(false);
    this.loading.set(false);
    this.searchFocused.set(false);
    this.isDropdownOpen.set(false);
    this.highlightedResultIndex.set(-1);
  };

  public readonly setInputValue = (value: string | null | undefined) => {
    this._value.set(value);
  };

  public readonly closeSurfaces = (): void => {
    this.cancelPendingSearch();
    this.pendingDirectSelect.set(false);
    this.searchFocused.set(false);
    this.isDropdownOpen.set(false);
    this.panelExpanded.set(false);
  };

  /** Dismissing a surface hands the caret back to the search field. The expanded
   *  modal runs without a focus trap, so nothing restores focus on close and it
   *  died on `<body>` — the host field (e.g. the Artikelsuche in a grid cell)
   *  then answered no keystroke. Reopening is suppressed: coming back to the
   *  field is not a new search. */
  private readonly restoreTriggerFocus = (): void => {
    if (this.readonly() || this.inline() || this.isRow()) return;
    const input = this.searchInput();
    if (!input) return;
    this.suppressReopen = true;
    input.focus();
    setTimeout(() => (this.suppressReopen = false));
  };

  public readonly clearSearch = (): void => {
    this.cancelPendingSearch();
    this.pendingDirectSelect.set(false);
    this.resetSearchTerm();
    this.highlightedResultIndex.set(-1);
    this.previewObject.set(null);
    this.selectedObjects.set([]);
    this.search();
    if (this.inline()) {
      requestAnimationFrame(() => this.modalSearchInput()?.focus());
    }
  };

  private resetSearchTerm(): void {
    this.value.set('');
    this._value.set('');
    this.searched.set(false);
    this.tableNavigation.set(false);
  }

  private debounceHandle: ReturnType<typeof setTimeout> | null = null;

  private cancelPendingSearch(): void {
    if (this.debounceHandle) {
      clearTimeout(this.debounceHandle);
      this.debounceHandle = null;
    }
    this.pendingSearch.set(false);
  }

  public readonly scheduleSearch = () => {
    if (this.debounceHandle) {
      clearTimeout(this.debounceHandle);
      this.debounceHandle = null;
    }

    this.abortInFlight()?.();

    if (!this._value()) {
      this.pendingSearch.set(false);
      this.search();
      return;
    }

    this.pendingSearch.set(true);
    this.debounceHandle = setTimeout(() => {
      this.search();
      this.debounceHandle = null;
    }, 300);
  };

  private flushSearch(): void {
    if (this.debounceHandle) {
      clearTimeout(this.debounceHandle);
      this.debounceHandle = null;
      this.search();
    }
  }

  /** Focusing the modal/inline search field (click, Ctrl+F, programmatic) takes
   *  the single focus back — neither the highlight frame nor the single-select
   *  preview ring may survive it. */
  public readonly onModalSearchFocusIn = (): void => {
    if (this.tableNavigation()) this.tableNavigation.set(false);
    if (this.highlightedResultIndex() >= 0) this.highlightedResultIndex.set(-1);
    if (this.previewObject() !== null) this.previewObject.set(null);
  };

  public readonly onInputChange = (value: string | null | undefined) => {
    this.searchFocused.set(true);
    this.setInputValue(value);
    this.tableNavigation.set(false);
    this.highlightedResultIndex.set(-1);
    this.searched.set(false);
    this.pendingDirectSelect.set(false);
    this.scheduleSearch();
  };

  public readonly onTriggerInput = (value: string | null | undefined) => {
    if (this.selectedObject()) {
      this.selectedObject.set(null);
      this.onInputChange('');
      return;
    }
    this.onInputChange(value);
  };

  public readonly onTriggerClear = (): void => {
    this.selectedObject.set(null);
    this.searched.set(false);
    this.onInputChange('');
  };

  public readonly onDeleteSelection = (event: Event): void => {
    if (this.readonly()) return;
    if (this.multiSelecting() && this.selectedObjects().length > 0) {
      event.preventDefault();
      this.clearPicks();
      return;
    }
    if (!this.selectedObject()) return;
    event.preventDefault();
    this.onTriggerClear();
  };
  public readonly openExpanded = (): void => {
    if (this.readonly() || this.inline()) return;
    this.setFocused();
    this.measureDropdownMaxHeight();
    this.panelExpanded.set(true);
    this.isDropdownOpen.set(true);
    // Fieldok contract: an untouched expanded view lists the first page right
    // away (empty term = browse) instead of the "Suchbegriff eingeben" prompt.
    if (!this.searched()) this.search();
  };

  public readonly resetSearch = (): void => {
    this._value.set('');
    this.typeFilter.set(null);
    this.numberFilter.set(null);
    this.cityFilter.set(null);
    this.showDeleted.set(false);
    this.showInactive.set(false);
    this.searched.set(false);
    this.search();
  };

  public readonly onArrowDown = (event: Event): void => {
    if (this.isSurfaceOpen() || !this.searchFocused()) {
      return;
    }
    this.moveHighlight(1, event);
  };

  public readonly onArrowUp = (event: Event): void => {
    if (this.isSurfaceOpen() || !this.searchFocused()) {
      return;
    }
    this.moveHighlight(-1, event);
  };

  public readonly onEnterSelect = (event: Event): void => {
    if (!this.searchFocused()) {
      return;
    }

    if (this.highlightedResultIndex() >= 0) {
      return;
    }

    if (this.usePreviewFlow() && this.previewObject()) {
      return;
    }

    const direct = this.directKeyMatch();
    if (direct) {
      (event as KeyboardEvent).preventDefault();
      this.directSelect(direct);
      return;
    }

    if (this.searching()) {
      (event as KeyboardEvent).preventDefault();
      this.deferDirectSelect();
      // A disabled dropdown shows nothing of the running query, so waiting for it
      // reads as a swallowed Enter — escalate now; a landing exact key still wins.
      if (this.legacySlots() && !this.isExpandedView() && !!this._value()) {
        this.openExpanded();
      }
      return;
    }

    if (!this.inline() && !this.isExpandedView() && !!this._value()) {
      (event as KeyboardEvent).preventDefault();
      this.openExpanded();
    }
  };

  public readonly onTabSelect = (event: Event): void => {
    if (!this.searchFocused()) {
      return;
    }
    if (this.highlightedResultIndex() >= 0) {
      return;
    }
    const direct = this.directKeyMatch();
    if (direct) {
      (event as KeyboardEvent).preventDefault();
      this.directSelect(direct);
      return;
    }
    if (this.searching()) {
      (event as KeyboardEvent).preventDefault();
      this.deferDirectSelect();
      return;
    }
    if (!this.inline() && !this.isExpandedView() && !!this._value()) {
      (event as KeyboardEvent).preventDefault();
      this.openExpanded();
    }
  };

  private deferDirectSelect(): void {
    this.pendingDirectSelect.set(true);
    this.flushSearch();
  }

  private readonly directSelect = (result: any): void => {
    if (this.multiSelecting()) {
      this.toggleChecked(result);
      this.highlightedResultIndex.set(-1);
      return;
    }
    this.selectResult(result);
  };

  private readonly previewOnSheet = (entity: any): void => {
    this.previewObject.set(entity);
    this.highlightedResultIndex.set(this.navRows().indexOf(entity));
  };

  public readonly selectResult = (result: any): void => {
    if (this.multiSelecting()) {
      this.toggleChecked(result);
      this.onConfirmedManyFromCollapsed();
      return;
    }
    if (this.mobileSheet()) {
      this.previewOnSheet(result);
      return;
    }
    this.selectedObject.set(result);
    this.searchFocused.set(false);
    this.highlightedResultIndex.set(-1);
    this.panelExpanded.set(false);
    this.isDropdownOpen.set(false);
    this.recordLastSearch();
    this.setLastUsed(result);
  };

  private readonly onConfirmedManyFromCollapsed = (): void => {
    this.confirmedMany.emit(this.selectedObjects());
  };

  private maybeLoadMore(el: HTMLElement | null): void {
    if (!el) return;
    if (!this.hasMore() || this.loadingMore() || this.loading()) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining <= 96) {
      this.loadMore.emit();
    }
  }

  protected onResultsScroll(event: Event): void {
    if (this.panelExpanded() && !this.mobileSheet()) return;
    this.maybeLoadMore(event.target as HTMLElement | null);
  }

  protected onModalResultsScroll(event: Event): void {
    if (!this.isExpandedView() || this.modalTab() !== 'results') return;
    this.maybeLoadMore(event.target as HTMLElement | null);
  }

  public readonly selectFromHistory = (entity: any): void => {
    if (this.mobileSheet() && !this.multiSelecting()) {
      this.previewOnSheet(entity);
      return;
    }
    this.selectedObject.set(entity);
    this.searchFocused.set(false);
    this.setLastUsed(entity);
  };

  /** In multiselect a row click only focuses/previews — checking is exclusive to
   *  the checkbox (and Space), so a click to inspect a row never flips its pick. */
  public readonly onTableRowClick = (result: any): void => {
    this.highlightedResultIndex.set(this.activeModalEntities().indexOf(result));
    this.focusResults();
    if (this.multiSelecting() || this.usePreviewFlow()) {
      this.previewObject.set(result);
    } else {
      this.selectResultFromModal(result);
    }
  };

  /** Double-click takes the row straight away — in multiselect it joins the
   *  checked set and commits without checkbox or "übernehmen" (F12). */
  public readonly onTableRowDblClick = (result: any): void => {
    if (this.multiSelecting()) {
      if (!this.isChecked(result)) this.toggleChecked(result);
      this.confirmMultiSelection();
      return;
    }
    if (this.usePreviewFlow()) this.confirmPreview();
  };

  public readonly previewResultInModal = (result: any): void => {
    this.previewObject.set(result);
  };

  public readonly selectResultFromModal = (result: any): void => {
    if (this.multiSelecting()) {
      this.toggleChecked(result);
      return;
    }
    this.selectedObject.set(result);
    this.highlightedResultIndex.set(-1);
    this.confirmModalSelection();
  };

  public readonly confirmPreview = (): void => {
    const preview = this.previewObject();
    if (!preview) return;
    this.selectedObject.set(preview);
    this.highlightedResultIndex.set(-1);
    this.confirmModalSelection();
  };

  /** Enter typed into the expanded/inline search field commits ONLY the checked
   *  multiselect picks ("übernehmen" without reaching for the button). Anything
   *  else keeps the field's pre-existing Enter behavior (exact-key direct
   *  select via the input handler). */
  private onEnterInExpandedField(event: Event): void {
    if (!this.isExpandedView() || this.tableRowKeyboard()) return;
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target?.closest('.masterdata-search-modal-search')) return;
    if (!this.multiSelecting() || this.selectedObjects().length === 0) return;
    event.preventDefault();
    this.confirmMultiSelection();
  }

  public readonly dismissModal = (): void => {
    this.suppressReopen = true;
    setTimeout(() => (this.suppressReopen = false));
    this.searchFocused.set(false);
    this.panelExpanded.set(false);
    this.highlightedResultIndex.set(-1);
    this.isDropdownOpen.set(false);
    this.typeFilter.set(null);
    this.numberFilter.set(null);
    this.cityFilter.set(null);
    this.showDeleted.set(false);
    this.showInactive.set(false);
    this.cancelPendingSearch();
    this.resetSearchTerm();
    this.previewObject.set(null);
    this.detailCollapsed.set(true);
    this.closed.emit();
    this.restoreTriggerFocus();
    this.resetInlineAfterClose();
  };

  public readonly confirmModalSelection = (): void => {
    const selected = this.selectedObject();
    if (selected) {
      this.recordLastSearch();
      this.setLastUsed(selected);
    }
    this.suppressReopen = true;
    setTimeout(() => (this.suppressReopen = false));
    this.panelExpanded.set(false);
    this.searchFocused.set(false);
    this.isDropdownOpen.set(false);
    this.previewObject.set(null);
    this.resetSearchTerm();
    this.resetInlineAfterClose();
  };

  /** The inline view (embedded one-shot picker) has no overlay close to reset it —
   *  restore the fresh boot state (empty term, no picks, refetched full list,
   *  results tab, first-row highlight) after a confirm or dismiss, so a reopened
   *  Swing dialog never shows the previous session's results or checkmarks. */
  private readonly resetInlineAfterClose = (): void => {
    if (!this.inline()) return;
    this.selectedObjects.set([]);
    this.modalTab.set('results');
    this.search();
    requestAnimationFrame(() => this.modalSearchInput()?.focus());
  };

  constructor() {
    afterNextRender(() => {
      if (!this.inline()) return;
      this.search();
      requestAnimationFrame(() => {
        if (this._value()) this.focusResults();
        else this.modalSearchInput()?.focus();
      });
    });

    // only where the toggle is actually offered — never on the mobile sheet,
    // which hides the switch and would trap the user in multiselect
    effect(() => {
      if (!this.isExpandedView() || this.mobileSheet()) return;
      if (!this.multiToggleDefault() || !this.multiToggle() || this.multiple()) return;
      this.multiSelectOverride.set(true);
    });

    effect(() => {
      if (!this.isExpandedView() || this.searching()) return;
      const rows = this.navRows();
      const previewFollows = this.highlightDrivesPreview();
      if (rows.length === 0) {
        this.anchoredFirstRow = null;
        // The table lost (or took over) nothing it could own — hand the focus
        // back to the search field so the user can correct the term right away.
        // Tracked read: arming AFTER an empty result landed must also bounce.
        if (this.tableNavigation()) this.focusSearchField();
        return;
      }
      // A changed first-row identity means a fresh result set (a load-more append
      // keeps it) — every restarted query re-anchors at the first row. Anchoring
      // only happens while the table owns the focus; while the field does, no
      // row carries the blue frame (single-focus contract).
      const newResultSet = rows[0] !== this.anchoredFirstRow;
      this.anchoredFirstRow = rows[0];
      if (!this.tableNavigation()) return;
      const index = untracked(this.highlightedResultIndex);
      const next = newResultSet || index < 0 ? 0 : Math.min(index, rows.length - 1);
      if (next !== index) this.highlightedResultIndex.set(next);
      if (!previewFollows) return;
      const target = rows[next];
      if (target && untracked(this.previewObject) !== target) {
        this.previewObject.set(target);
      }
    });

    effect(() => {
      const preview = this.previewObject();
      const visible = this.mobileSheet() ? this.navRows() : this.activeModalEntities();
      if (preview && !visible.includes(preview)) {
        this.previewObject.set(null);
      }
    });

    effect(() => {
      if (this.modalTab() === 'selected' && !this.multiSelecting()) {
        this.modalTab.set('results');
      }
    });

    let wasExpanded = false;
    afterRenderEffect(() => {
      const expanded = this.panelExpanded();
      if (expanded && !wasExpanded && !this.mobileSheet()) {
        const input = this.modalSearchInput();
        // Opening with a term puts the single focus into the table (row 0 gets
        // the frame); an empty field keeps it in the search input.
        requestAnimationFrame(() => {
          if (this._value()) this.focusResults();
          else input?.focus();
        });
      }
      wasExpanded = expanded;
    });

    afterRenderEffect(() => {
      const highlightedIndex = this.highlightedResultIndex();
      const count = this.navRows().length;
      if (highlightedIndex < 0 || highlightedIndex >= count) {
        this.lastScrolledIndex = -1;
        return;
      }
      if (highlightedIndex === this.lastScrolledIndex) return;
      this.lastScrolledIndex = highlightedIndex;
      const doc = typeof document !== 'undefined' ? document : null;
      const scroller = doc?.querySelector(
        '.soft-overlay-content:has(.masterdata-search-list, .masterdata-search-chips, .masterdata-search-table), .masterdata-search-inline-body:has(.masterdata-search-list, .masterdata-search-chips, .masterdata-search-table)'
      );
      const activeElement = scroller?.querySelector(
        `[data-result-index="${highlightedIndex}"]`
      ) as HTMLElement | null;
      if (!activeElement) return;
      if (!this.scrollRowIntoTable(activeElement)) {
        activeElement.scrollIntoView({ block: 'nearest' });
      }
    });

    afterRenderEffect(() => {
      if (!this.pendingDirectSelect() || this.searching()) {
        return;
      }
      this.pendingDirectSelect.set(false);
      const direct = this.directKeyMatch();
      if (direct) {
        this.directSelect(direct);
        return;
      }
      // Enter/Tab was pressed while the search was still in flight and the
      // settled result holds no exact hit — escalate to the extended view.
      if (!this.inline() && !this.isExpandedView() && this._value()) {
        this.openExpanded();
      }
    });
  }

  public readonly onOverlayOpenChange = (open: boolean): void => {
    if (!open) {
      this.searchFocused.set(false);
      this.panelExpanded.set(false);
      this.highlightedResultIndex.set(-1);
      this.isDropdownOpen.set(false);
      this.typeFilter.set(null);
      this.numberFilter.set(null);
      this.showDeleted.set(false);
      this.showInactive.set(false);
      this.cancelPendingSearch();
      this.pendingDirectSelect.set(false);
      this.resetSearchTerm();
      this.previewObject.set(null);
      this.detailCollapsed.set(true);
      this.closed.emit();
      if (this.isRow()) this.fieldRow()?.focus();
      else this.restoreTriggerFocus();
    }
  };
}

/** Vertical layout position of an element in the document, summed over the
    offsetParent chain; unaffected by transforms and scroll positions. */
function layoutTop(element: HTMLElement): number {
  let top = 0;
  let current: HTMLElement | null = element;
  while (current) {
    top += current.offsetTop;
    current = current.offsetParent as HTMLElement | null;
  }
  return top;
}
